import React, { Component } from "react";
import { Link } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Posts.css";

const STATUS_OPTIONS = [
  { value: "2", label: "Draft", className: "is-draft" },
  { value: "3", label: "Request", className: "is-request" },
  { value: "4", label: "Review Completed", className: "is-review-completed" },
  { value: "1", label: "Published", className: "is-published" },
  { value: "0", label: "Inactive", className: "is-inactive" },
];

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const humanizeKey = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const plainText = (value) =>
  String(value ?? "")
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();

class Posts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      posts: [],
      loading: true,
      searchText: "",
      statusFilter: "all",
      currentPage: 1,
      pageSize: 10,
      viewingId: null,
    };
  }

  componentDidMount() {
    this.fetchPosts();
  }

  getBaseUrl = () =>
    axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, "") : "";

  fetchPosts = async () => {
    this.setState({ loading: true });
    try {
      const res = await axios.get("/posts");
      if (res.data?.status === "success") {
        const received = Array.isArray(res.data.data) ? res.data.data : [];
        this.setState({ posts: received, loading: false, currentPage: 1 });
      } else {
        this.setState({ loading: false });
        Swal.fire("Error", res.data?.message || "Failed to load posts", "error");
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      this.setState({ loading: false });
      Swal.fire("Error", "Failed to fetch posts", "error");
    }
  };

  getStatusValue = (post) => {
    if (post?.post_state !== null && post?.post_state !== undefined && String(post.post_state) !== "") {
      const state = String(post.post_state);
      return ["0", "1", "2", "3", "4"].includes(state) ? state : "unknown";
    }

    const status = String(post?.status ?? "").toLowerCase();
    if (status.includes("draft")) return "2";
    if (status.includes("request") || status.includes("pending")) return "3";
    if (status.includes("review") || status.includes("complete")) return "4";
    if (status.includes("inactive")) return "0";
    if (status.includes("active") || status.includes("publish")) return "1";
    return "unknown";
  };

  getStatusMeta = (postOrValue) => {
    const value =
      typeof postOrValue === "object" && postOrValue !== null
        ? this.getStatusValue(postOrValue)
        : String(postOrValue ?? "unknown");
    const found = STATUS_OPTIONS.find((item) => item.value === value);
    return found || { value: "unknown", label: "Unknown", className: "is-unknown" };
  };

  handleSearchChange = (event) => {
    this.setState({ searchText: event.target.value, currentPage: 1 });
  };

  handleStatusFilterChange = (event) => {
    this.setState({ statusFilter: event.target.value, currentPage: 1 });
  };

  handlePageSizeChange = (event) => {
    this.setState({ pageSize: Number(event.target.value) || 10, currentPage: 1 });
  };

  handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this post?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.delete(`/posts/${id}`);
      if (res.data?.status === "success") {
        await Swal.fire({
          title: "Deleted",
          text: res.data.message || "Post deleted successfully.",
          icon: "success",
          confirmButtonColor: "#0049b0",
        });
        this.fetchPosts();
      } else {
        Swal.fire("Error", res.data?.message || "Failed to delete post", "error");
      }
    } catch (err) {
      console.error("Error deleting post:", err);
      Swal.fire("Error", err?.response?.data?.message || "Failed to delete post", "error");
    }
  };

  getUploadUrl = (filename) => {
    if (!filename) return "";
    const value = String(filename).trim();
    if (/^https?:\/\//i.test(value)) return value;
    const safeFilename = value.split(/[\\/]/).pop();
    return `${this.getBaseUrl()}/uploads/posts/${encodeURIComponent(safeFilename)}`;
  };

  parseGallery = (raw) => {
    if (!raw) return [];
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => (typeof item === "string" ? item : item?.filename || item?.image || ""))
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  handleView = async (id) => {
    this.setState({ viewingId: id });
    try {
      // const response = await axios.get(`/posts/${id}`);
      // http://localhost/travelapi/api/get_post_by_id?id=16

      const response = await axios.get(`/posts/get_post_by_id?id=${id}`);
      if (response.data?.status !== "success") {
        Swal.fire("Error", response.data?.message || "Could not load post details.", "error");
        return;
      }

      const data = response.data.data || {};
      const featureFilename = data.feature_image || data.image_id;
      const featureImage = featureFilename
        ? `<img class="posts-view-feature" src="${escapeHtml(this.getUploadUrl(featureFilename))}" alt="Featured property image" />`
        : `<div class="posts-view-image-empty">No featured image</div>`;

      const gallery = this.parseGallery(data.gallery_images);
      const galleryHtml = gallery.length
        ? `<div class="posts-view-gallery">${gallery
            .map(
              (filename, index) =>
                `<img src="${escapeHtml(this.getUploadUrl(filename))}" alt="Gallery image ${index + 1}" />`
            )
            .join("")}</div>`
        : `<p class="posts-view-muted">No gallery images saved.</p>`;

      const excluded = new Set([
        "content",
        "gallery_images",
        "feature_image",
        "image_id",
        "more_night_discount",
      ]);
      const valuesHtml = Object.entries(data)
        .filter(([key, value]) => !excluded.has(key) && value !== null && value !== undefined)
        .map(([key, value]) => {
          const rawValue = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
          const displayValue = rawValue.trim() === "" ? "N/A" : rawValue;
          return `<tr><th>${escapeHtml(humanizeKey(key))}</th><td>${escapeHtml(displayValue)}</td></tr>`;
        })
        .join("");

      let discounts = data.more_night_discount;
      if (typeof discounts === "string") {
        try {
          discounts = JSON.parse(discounts);
        } catch (error) {
          /* keep original string */
        }
      }
      const discountDisplay = discounts
        ? typeof discounts === "object"
          ? JSON.stringify(discounts, null, 2)
          : String(discounts)
        : "N/A";
      const description = plainText(data.content) || "No description provided.";
      const status = this.getStatusMeta(data);

      const html = `
        <div class="posts-view-content">
          <div class="posts-view-hero">
            <div>${featureImage}</div>
            <div class="posts-view-hero-copy">
              <span class="posts-status-badge ${status.className}"><span class="posts-status-dot"></span>${escapeHtml(status.label)}</span>
              <h3>${escapeHtml(data.title || "Untitled listing")}</h3>
              <p>${escapeHtml(data.real_address || data.location || "Location not provided")}</p>
              <div class="posts-view-price">${
                data.sales_price !== null && data.sales_price !== undefined && data.sales_price !== ""
                  ? `৳${escapeHtml(data.sales_price)}`
                  : "Price not set"
              }</div>
            </div>
          </div>
          <section class="posts-view-section">
            <h4>Listing Details</h4>
            <div class="posts-view-table-wrap"><table class="posts-view-table"><tbody>${
              valuesHtml || "<tr><td>No additional details returned by the API.</td></tr>"
            }</tbody></table></div>
          </section>
          <section class="posts-view-section">
            <h4>Description</h4>
            <p class="posts-view-description">${escapeHtml(description)}</p>
          </section>
          <section class="posts-view-section">
            <h4>Long-stay Discounts</h4>
            <pre class="posts-view-pre">${escapeHtml(discountDisplay)}</pre>
          </section>
          <section class="posts-view-section">
            <h4>Gallery Images</h4>
            ${galleryHtml}
          </section>
        </div>`;

      await Swal.fire({
        title: "Post Details",
        html,
        width: "980px",
        confirmButtonText: "Close",
        confirmButtonColor: "#0049b0",
        customClass: { popup: "posts-view-popup" },
      });
    } catch (error) {
      console.error("Error loading post details:", error);
      Swal.fire("Error", error?.response?.data?.message || "Failed to load post details.", "error");
    } finally {
      this.setState({ viewingId: null });
    }
  };

  renderPagination = (totalPages, currentPage) => {
    if (totalPages <= 1) return null;
    let pages = [];
    if (totalPages <= 7) {
      pages = Array.from({ length: totalPages }, (_, index) => index + 1);
    } else {
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      pages = [1];
      if (start > 2) pages.push("left-dots");
      for (let page = start; page <= end; page += 1) pages.push(page);
      if (end < totalPages - 1) pages.push("right-dots");
      pages.push(totalPages);
    }

    return (
      <div className="posts-pagination" aria-label="Posts pagination">
        <button
          type="button"
          className="posts-page-btn posts-page-nav"
          disabled={currentPage <= 1}
          onClick={() => this.setState({ currentPage: currentPage - 1 })}
          aria-label="Previous page"
        >
          <i className="ph ph-caret-left" />
        </button>
        {pages.map((page) =>
          typeof page === "string" ? (
            <span className="posts-page-dots" key={page}>
              …
            </span>
          ) : (
            <button
              type="button"
              key={page}
              className={`posts-page-btn ${page === currentPage ? "active" : ""}`}
              aria-current={page === currentPage ? "page" : undefined}
              onClick={() => this.setState({ currentPage: page })}
            >
              {page}
            </button>
          )
        )}
        <button
          type="button"
          className="posts-page-btn posts-page-nav"
          disabled={currentPage >= totalPages}
          onClick={() => this.setState({ currentPage: currentPage + 1 })}
          aria-label="Next page"
        >
          <i className="ph ph-caret-right" />
        </button>
      </div>
    );
  };

  render() {
    const { posts, loading, searchText, statusFilter, currentPage, pageSize, viewingId } = this.state;
    const baseUrl = this.getBaseUrl();
    const normalizedSearch = searchText.trim().toLowerCase();

    const filteredPosts = posts.filter((post) => {
      const matchesStatus = statusFilter === "all" || this.getStatusValue(post) === statusFilter;
      const searchable = [
        post.title,
        post.category_name,
        post.customer_name,
        post.full_name,
        post.tag_ids,
        post.facility_ids,
        post.real_address,
        post.location,
        post.id,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();
      return matchesStatus && (!normalizedSearch || searchable.includes(normalizedSearch));
    });

    const totalPages = Math.max(1, Math.ceil(filteredPosts.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const pageStart = (safePage - 1) * pageSize;
    const pagePosts = filteredPosts.slice(pageStart, pageStart + pageSize);

    const counts = {
      total: posts.length,
      "1": posts.filter((post) => this.getStatusValue(post) === "1").length,
      "0": posts.filter((post) => this.getStatusValue(post) === "0").length,
      "2": posts.filter((post) => this.getStatusValue(post) === "2").length,
      "3": posts.filter((post) => this.getStatusValue(post) === "3").length,
      "4": posts.filter((post) => this.getStatusValue(post) === "4").length,
    };

    const statCards = [
      { key: "all", label: "Total Posts", count: counts.total, icon: "ph-stack", tone: "total" },
      { key: "1", label: "Published", count: counts["1"], icon: "ph-check-circle", tone: "published" },
      { key: "2", label: "Drafts", count: counts["2"], icon: "ph-file-dashed", tone: "draft" },
      { key: "3", label: "Requests", count: counts["3"], icon: "ph-clock-countdown", tone: "request" },
      { key: "4", label: "Review Completed", count: counts["4"], icon: "ph-seal-check", tone: "review" },
      { key: "0", label: "Inactive", count: counts["0"], icon: "ph-prohibit", tone: "inactive" },
    ];

    return (
      <div className="pc-container posts-management">
        <div className="pc-content">
          <div className="posts-card-header posts-page-header">
            <div className="posts-heading-area">
              <span className="posts-title-icon">
                <i className="ph ph-buildings" />
              </span>
              <div>
                <h3 className="posts-title">Posts Management</h3>
                <div className="posts-breadcrumb">
                  <span>Posts</span>
                  <i className="ph ph-caret-right" />
                  <span>List</span>
                </div>
              </div>
            </div>
            <Link to="/admin/post_form" className="posts-add-btn">
              <i className="ph ph-plus-circle" /> New Post
            </Link>
          </div>

          <div className="posts-stat-grid">
            {statCards.map((item) => (
              <button
                type="button"
                key={item.key}
                className={`posts-stat-card tone-${item.tone} ${
                  statusFilter === item.key ? "is-selected" : ""
                }`}
                onClick={() => this.setState({ statusFilter: item.key, currentPage: 1 })}
                aria-pressed={statusFilter === item.key}
              >
                <span className="posts-stat-icon">
                  <i className={`ph ${item.icon}`} />
                </span>
                <span className="posts-stat-copy">
                  <span>{item.label}</span>
                  <strong>{item.count}</strong>
                </span>
                <i className="ph ph-arrow-up-right posts-stat-arrow" />
              </button>
            ))}
          </div>

          <div className="card posts-card">
            <div className="posts-toolbar posts-toolbar-filtered">
              <div className="posts-summary">
                <span className="posts-summary-label">Showing matches</span>
                <span className="posts-summary-count">{filteredPosts.length}</span>
              </div>
              <div className="posts-filter-controls">
                <label className="posts-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="search"
                    value={searchText}
                    onChange={this.handleSearchChange}
                    placeholder="Search title, category, customer..."
                    aria-label="Search posts"
                  />
                  {searchText ? (
                    <button
                      type="button"
                      className="posts-search-clear"
                      onClick={() => this.setState({ searchText: "", currentPage: 1 })}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  ) : null}
                </label>
                <label className="posts-status-filter-label">
                  <span>Status</span>
                  <select
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                    aria-label="Filter posts by status"
                  >
                    <option value="all">All statuses</option>
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="posts-page-size">
                  <span>Rows</span>
                  <select value={pageSize} onChange={this.handlePageSizeChange} aria-label="Rows per page">
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="posts-table-shell">
              <div className="posts-table-scroll">
                <table className="table posts-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="posts-col-number">#</th>
                      <th className="posts-col-image">Image</th>
                      <th className="posts-col-image">Host ID</th>
                      <th className="posts-col-title">Title</th>
                      <th className="posts-col-category">Category</th>
                      <th className="posts-col-price">Price (BDT)</th>
                      <th className="posts-col-status">Status</th>
                      <th className="posts-col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="posts-empty-cell">
                          <div className="posts-loading-state">
                            <span className="posts-spinner" />
                            <strong>Loading posts...</strong>
                            <span>Please wait while your listings are loaded.</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredPosts.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="posts-empty-cell">
                          <div className="posts-no-data">
                            <span className="posts-empty-icon">
                              <i className="ph ph-magnifying-glass" />
                            </span>
                            <h5>{posts.length ? "No matching posts" : "No posts yet"}</h5>
                            <p>
                              {posts.length
                                ? "Try changing your search or status filter."
                                : "Create your first property listing to get started."}
                            </p>
                            {posts.length === 0 ? (
                              <Link to="/admin/post_form" className="posts-empty-btn">
                                Create New Post
                              </Link>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pagePosts.map((post, index) => {
                        const image = post.feature_image || post.image_id;
                        const status = this.getStatusMeta(post);
                        const regularPrice = post.regular_price;
                        const salesPrice = post.sales_price ?? post.price;

                        return (
                          <tr key={post.id ?? `${post.title}-${pageStart + index}`}>
                            <td className="posts-number-cell">{pageStart + index + 1}</td>
                            <td>
                              <div className="posts-image-wrap">
                                {image ? (
                                  <img
                                    className="posts-image"
                                    src={`${baseUrl}/uploads/posts/${encodeURIComponent(
                                      String(image).split(/[\\/]/).pop()
                                    )}`}
                                    alt={post.title ? `${post.title} cover` : "Post cover"}
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="posts-image-placeholder">
                                    <i className="ph ph-image" />
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <a className="posts-title-cell" title={post.title || "Untitled listing"}
                                href={`/listing/${post.id}`} target="_blank" rel="noopener noreferrer">
                                BK-HOST-{post.customer_id || "N/A"} 
                              </a>
                            </td>
                            <td>
                              <a className="posts-title-cell" title={post.title || "Untitled listing"}
                                href={`/listing/${post.id}`} target="_blank" rel="noopener noreferrer">
                                <br></br>
                                {post.title || "Untitled listing"}
                              </a>
                              {post.real_address || post.location ? (
                                <div
                                  className="posts-row-subtitle"
                                  title={post.real_address || post.location}
                                >
                                  <i className="ph ph-map-pin" /> {post.real_address || post.location}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              <span
                                className="posts-category-cell"
                                title={post.category_name || "Uncategorized"}
                              >
                                {post.category_name || "Uncategorized"}
                              </span>
                            </td>
                            <td>
                              <div className="posts-price-cell">
                                {regularPrice !== null &&
                                regularPrice !== undefined &&
                                regularPrice !== "" ? (
                                  <span className="posts-regular-price">৳{regularPrice}</span>
                                ) : null}
                                <span className="posts-sale-price">
                                  {salesPrice !== null && salesPrice !== undefined && salesPrice !== ""
                                    ? `৳${salesPrice}`
                                    : "Not set"}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className={`posts-status-badge ${status.className}`}>
                                <span className="posts-status-dot" />
                                {status.label}
                              </span>
                            </td>
                            <td>
                              <div className="posts-actions">
                                <button
                                  type="button"
                                  className="posts-action-btn posts-view-btn"
                                  onClick={() => this.handleView(post.id)}
                                  disabled={viewingId === post.id}
                                  title="View full post details"
                                >
                                  <i className="ph ph-eye" />
                                  {viewingId === post.id ? "Loading" : "View"}
                                </button>
                                <Link
                                  to={`/admin/post_form/${post.id}`}
                                  className="posts-action-btn posts-edit-btn"
                                  title="Edit post"
                                >
                                  <i className="ph ph-note-pencil" /> Edit
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => this.handleDelete(post.id)}
                                  className="posts-action-btn posts-delete-btn"
                                  title="Delete post"
                                >
                                  <i className="ph ph-trash" /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {!loading && filteredPosts.length > 0 ? (
              <div className="posts-pagination-bar">
                <div className="posts-pagination-info">
                  Showing <strong>{pageStart + 1}</strong>–
                  <strong>{Math.min(pageStart + pageSize, filteredPosts.length)}</strong> of{" "}
                  <strong>{filteredPosts.length}</strong> posts
                </div>
                {this.renderPagination(totalPages, safePage)}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

export default Posts;